import { useCallback, useEffect, useState } from "react";
import AdminAuthGuard from "../components/admin/AdminAuthGuard";
import AdminLayout from "../components/admin/AdminLayout";
import { listAdminPayments } from "../lib/api/index";
import { logger } from "../lib/logger";

function Inner({ admin }) {
  const [rows, setRows] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(25);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [statusFilter, setStatusFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  const fetchPage = useCallback(async (p, s) => {
    setLoading(true); setErr(null);
    try {
      const params = { page: p, page_size: pageSize };
      if (s) params.status_filter = s;
      const data = await listAdminPayments(params);
      setRows(data.rows);
      setTotal(data.total);
      setTotalPages(data.total_pages);
    } catch (e) {
      logger.error("admin_payments_list_failed", { status: e?.response?.status });
      setErr("Could not load payments.");
    } finally { setLoading(false); }
  }, [pageSize]);

  useEffect(() => { fetchPage(page, statusFilter); }, [fetchPage, page, statusFilter]);

  return (
    <AdminLayout admin={admin}>
      <div className="sf-eyebrow" style={{ marginBottom: 8 }}>Payments</div>
      <h1 className="sf-display" style={{ fontSize: 28, marginBottom: 18 }} data-testid="admin-payments-headline">
        All payments.
      </h1>

      <div className="sf-card" style={{ marginBottom: 16, display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }} data-testid="admin-payments-filters">
        <label className="sf-eyebrow" style={{ fontSize: 11 }}>Status
          <select value={statusFilter} onChange={(e) => { setPage(1); setStatusFilter(e.target.value); }}
                  className="sf-input" style={{ marginLeft: 8 }} data-testid="payments-filter-status">
            <option value="">all</option>
            <option value="pending">pending</option>
            <option value="paid">paid</option>
            <option value="failed">failed</option>
          </select>
        </label>
        <span className="sf-mono sf-muted-text" style={{ marginLeft: "auto", fontSize: 12 }} data-testid="payments-total">
          {total} total
        </span>
      </div>

      {err && <div className="sf-card" data-testid="admin-payments-error">{err}</div>}

      <div className="sf-card" style={{ padding: 0 }} data-testid="admin-payments-table">
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
          <thead>
            <tr style={{ textAlign: "left", color: "var(--sf-gold)", fontFamily: "var(--sf-mono)", fontSize: 11, letterSpacing: "0.08em" }}>
              <th style={{ padding: 10 }}>PAYMENT ID</th>
              <th style={{ padding: 10 }}>SESSION</th>
              <th style={{ padding: 10 }}>AMOUNT</th>
              <th style={{ padding: 10 }}>STATUS</th>
              <th style={{ padding: 10 }}>SOURCE</th>
              <th style={{ padding: 10 }}>PAID AT</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={6} style={{ padding: 16, color: "var(--sf-muted)" }}>Loading…</td></tr>}
            {!loading && rows.length === 0 && <tr><td colSpan={6} style={{ padding: 16, color: "var(--sf-muted)" }}>No payments match these filters.</td></tr>}
            {!loading && rows.map((p) => (
              <tr key={p.payment_id} style={{ borderTop: "1px solid #22262B" }} data-testid={`admin-payments-row-${p.payment_id}`}>
                <td style={{ padding: 10 }} className="sf-mono">{p.payment_id.slice(0, 24)}…</td>
                <td style={{ padding: 10 }} className="sf-mono">{(p.audit_session_id || "").slice(0, 12)}…</td>
                <td style={{ padding: 10 }} className="sf-mono">${(p.amount_cents / 100).toFixed(2)} {p.currency?.toUpperCase()}</td>
                <td style={{ padding: 10 }}>{p.status}</td>
                <td style={{ padding: 10 }} className="sf-mono sf-muted-text">{p.settlement_source || "—"}</td>
                <td style={{ padding: 10 }} className="sf-mono sf-muted-text">{p.paid_at || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div style={{ display: "flex", gap: 8, marginTop: 16, alignItems: "center", justifyContent: "flex-end" }} data-testid="admin-payments-pager">
        <button type="button" className="sf-btn-secondary" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))} data-testid="payments-page-prev">← Prev</button>
        <span className="sf-mono sf-muted-text" style={{ fontSize: 12 }}>Page {page} / {totalPages}</span>
        <button type="button" className="sf-btn-secondary" disabled={page >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))} data-testid="payments-page-next">Next →</button>
      </div>
    </AdminLayout>
  );
}

export default function AdminPayments() {
  return <AdminAuthGuard>{(admin) => <Inner admin={admin} />}</AdminAuthGuard>;
}
